"use client"

import * as React from "react"
import { SelectTrigger, SelectValue } from "@ui/shad"
import { cn } from "@ui/utils"

import { SelectInput } from "../forms/inputs/select-input"

interface TimeSelectProps {
  value?: string
  onChange?: (value: string) => void
  className?: string
  disabled?: boolean
  size?: "xs" | "sm" | "default"
  startHour?: number
  endHour?: number
  intervalMinutes?: number
  minTime?: string
  maxTime?: string
  includeNone?: boolean
  optionFilter?: (hour: number, minute: number) => boolean
  optionLabel?: (hour: number, minute: number) => string
}

function parseTime(value: string): { hour: number; minute: number } | null {
  const match = value.match(/^(\d{2}):(\d{2})$/)
  if (!match) return null

  const hour = Number(match[1])
  const minute = Number(match[2])

  if (Number.isNaN(hour) || Number.isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null
  }

  return { hour, minute }
}

function formatTimeLabel(hour: number, minute: number): string {
  const hour12 = hour % 12 || 12
  const period = hour >= 12 ? "PM" : "AM"
  return `${hour12}:${minute.toString().padStart(2, "0")} ${period}`
}

export function getNearestTimeSlot(date: Date): string {
  const hours = date.getHours()
  const minutes = date.getMinutes()

  if (hours < 8) {
    return "08:00"
  }

  if (hours > 18 || (hours === 18 && minutes > 0)) {
    return "18:00"
  }

  const roundedMinutes = Math.ceil(minutes / 30) * 30
  const finalHours = roundedMinutes === 60 ? hours + 1 : hours
  const finalMinutes = roundedMinutes === 60 ? 0 : roundedMinutes

  return `${String(finalHours).padStart(2, "0")}:${String(finalMinutes).padStart(2, "0")}`
}

export function TimeSelect({
  value,
  onChange,
  className,
  disabled,
  size = "default",
  startHour = 8,
  endHour = 18,
  intervalMinutes = 30,
  minTime,
  maxTime,
  includeNone = true,
  optionFilter,
  optionLabel,
}: TimeSelectProps) {
  const timeOptions = React.useMemo(() => {
    const options: { value: string; label: string }[] = []

    if (includeNone) {
      options.push({ value: "none", label: "None" })
    }
    const safeStart = Math.max(0, Math.min(23, startHour))
    const safeEnd = Math.max(safeStart, Math.min(23, endHour))
    const step = Math.min(60, Math.max(1, intervalMinutes))

    let minHour = 0
    let minMinute = 0
    if (minTime) {
      const [h, m] = minTime.split(":").map(Number)
      minHour = h
      minMinute = m
    }

    let maxHour = 23
    let maxMinute = 59
    if (maxTime) {
      const [h, m] = maxTime.split(":").map(Number)
      maxHour = h
      maxMinute = m
    }

    for (let hour = safeStart; hour <= safeEnd; hour++) {
      for (let minute = 0; minute < 60; minute += step) {
        if (optionFilter && !optionFilter(hour, minute)) {
          continue
        }
        if (minTime && (hour < minHour || (hour === minHour && minute <= minMinute))) {
          continue
        }
        if (maxTime && (hour > maxHour || (hour === maxHour && minute > maxMinute))) {
          continue
        }

        const value = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`
        const label = optionLabel?.(hour, minute) ?? formatTimeLabel(hour, minute)
        options.push({ value, label })
      }
    }

    const parsedValue = value ? parseTime(value) : null
    const hasSelectedValue = value ? options.some((option) => option.value === value) : false

    if (value && parsedValue && !hasSelectedValue) {
      const { hour, minute } = parsedValue
      const withinHourRange = hour >= safeStart && hour <= safeEnd
      const afterMin = !minTime || hour > minHour || (hour === minHour && minute > minMinute)
      const beforeMax = !maxTime || hour < maxHour || (hour === maxHour && minute <= maxMinute)
      const passesFilter = !optionFilter || optionFilter(hour, minute)

      if (withinHourRange && afterMin && beforeMax && passesFilter) {
        options.push({ value, label: optionLabel?.(hour, minute) ?? formatTimeLabel(hour, minute) })
        options.sort((a, b) => a.value.localeCompare(b.value))
      }
    }

    return options
  }, [startHour, endHour, intervalMinutes, minTime, maxTime, value, includeNone, optionFilter, optionLabel])

  const sizeClasses = {
    xs: "h-7 space-y-0",
    sm: "h-8 space-y-0",
    default: "space-y-0",
  }

  const triggerSizeClasses = {
    xs: "h-7 px-2 text-sm",
    sm: "h-8 px-3 text-sm",
    default: "px-3",
  }

  return (
    <SelectInput
      id="time-select"
      value={value}
      onChange={onChange}
      options={timeOptions}
      placeholder="Select time"
      disabled={disabled}
      className={cn(sizeClasses[size], className)}
      triggerButton={
        <SelectTrigger
          className={cn(
            "border-input hover:bg-accent rounded-md border bg-transparent focus:ring-1",
            triggerSizeClasses[size]
          )}
        >
          <SelectValue placeholder="Select time" />
        </SelectTrigger>
      }
    />
  )
}
